import { create } from "zustand";
import { io, Socket } from "socket.io-client";
import { api } from "@/services/api";
import { getPilotoUUID } from "@/services/getUser";

// Definir as constantes

const API_BASE_URL =
  import.meta.env.MODE === "development"
    ? "http://localhost:3000/api"
    : "https://api.piloto.live/api";

const SOCKET_URL =
  import.meta.env.MODE === "development"
    ? "http://localhost:3000"
    : "https://api.piloto.live/";

// Variável para armazenar a conexão de socket atual
let socketRef: Socket | null = null;

// Variável para armazenar o intervalo de polling do QR code
let qrCodePollingInterval: NodeJS.Timeout | null = null;

// Armazenar sockets ativos
interface SocketStorage {
  [sessionId: string]: Socket;
}

// Criar um objeto para armazenar os sockets
const socketStorage: SocketStorage = {};

// Tipos
export interface WhatsAppMessage {
  id: string;
  fromMe: boolean;
  senderId: string;
  text: string;
  data: any[];
  content: string;
  timestamp: Date;
  status: "sending" | "sent" | "delivered" | "read" | "failed" | "pending";
  type: "text" | "image" | "video" | "audio" | "document";
  role?: "user" | "assistant" | "system"; // Campo opcional para compatibilidade com UI de chat
  mediaUrl?: string;
  audioUrl?: string;
}

export interface WhatsAppContact {
  id: string;
  name: string;
  phoneNumber: string;
  avatar?: string;
  isOnline?: boolean;
}

export interface WhatsAppConversation {
  id: string;
  name: string;
  phoneNumber: string;
  avatar?: string;
  lastMessage?: WhatsAppMessage;
  unreadCount: number;
  messages: WhatsAppMessage[];
  loading: boolean;
  error: string | null;
  contact: WhatsAppContact;
  IaResponse?: boolean;
  threadId?: string;
}

export interface WhatsAppChats {
  id: string;
  profilePicUrl: string;
  pushName: string;
  remoteJid: string;
  updatedAt: Date; // ou Date, se você for fazer parse
  windowActive: boolean;
  windowExpires: string; // ou Date
  windowStart: string; // ou Date
}

export interface WhatsAppInstance {
  id: string;
  name: string;
  sessionId: string;
  status: "disconnected" | "connecting" | "connected" | "ready";
  /** De qual mundo veio. 'meta' NAO tem QR: a Cloud API conecta por Embedded
   *  Signup, nao por leitura de codigo. */
  provedor?: "meta" | "evolution";
  qrCode: string | null;
  phoneNumber: string | null;
  conversations: WhatsAppConversation[];
  lastActive: Date;
}

interface WhatsAppStore {
  instances: WhatsAppInstance[];
  activeInstance: string | null;
  loading: boolean;
  error: string | null;
  messages: WhatsAppMessage[];
  conversations: any[];
  currentSessionId: string | null;
  suggestedMessage: string;
  newMessage: string;

  setCurrentSessionId: (sessionId: string | null) => void;

  // Funções para gerenciar instâncias
  getInstances: () => WhatsAppInstance[];
  fetchWhatsAppInstances: () => Promise<void>;
  createWhatsAppInstance: (name: string, assistantId?: string) => Promise<boolean>;
  disconnectWhatsAppInstance: (instanceId: string) => Promise<boolean>;
  updateInstances: (
    updatedConversations: WhatsAppInstance[],
    targetInstanceId: string
  ) => void;
  subscribeToInstanceUpdates: (updatedInstances: WhatsAppInstance[]) => void;

  // Funções para limpar mensagen sugerida
  clearSuggestedMessage: () => void;
  handleChangeNewMessage: (newMessage: string) => void;

  // Funções para gerenciar sockets
  connectSocket: (sessionId: string) => void;
  disconnectSocket: (sessionId: string) => void;

  // Funções para gerenciar QR codes
  fetchQRCode: (sessionId: string) => Promise<string | null>;
  startQRCodePolling: (sessionId: string) => void;
  stopQRCodePolling: () => void;
  subscribeToQRCodeUpdates: ({
    sessionId,
    qrCode,
  }: {
    sessionId: string;
    qrCode: string | null;
  }) => void;

  // Funções para gerenciar conversas
  fetchConversations: (instanceId?: string) => Promise<any>;
  fetchConversationsSimple: (instanceId: string) => Promise<any>;
  fetchChatMessages: (instanceId: string, chatId: string) => Promise<any>;

  fetchMessages: (conversationId: string, instanceId?: string) => Promise<void>;
  sendMessage: (
    conversationId: string,
    content: string,
    instanceId?: string
  ) => Promise<void>;
  sendAudio: (conversationId: string, audioBlob: Blob) => Promise<void>;
  updateHistory: (message: WhatsAppMessage) => void;
}

export const useWhatsAppStore = create<WhatsAppStore>((set, get) => {
  return {
    instances: [],
    activeInstance: null,
    loading: false,
    error: null,
    currentSessionId: null,
    messages: [],
    conversations: [],
    suggestedMessage: "",
    newMessage: "",

    getInstances: () => {
      return get().instances;
    },

    setCurrentSessionId: (sessionId) => set({ currentSessionId: sessionId }),

    fetchWhatsAppInstances: async () => {
      try {
        set({ loading: true, error: null });
        console.log(
          "[WhatsApp Store] Iniciando busca de instâncias WhatsApp..."
        );

        // Evolution v2 (já escopado ao dono no backend): lista as instâncias do usuário.
        const response = await api.get(`api/whatsapp/v2/instance/fetchInstances`);
        const list: any[] = Array.isArray(response.data)
          ? response.data
          : response.data?.data ?? [];
        const mapStatus = (s: any): WhatsAppInstance["status"] =>
          s === "open" ? "connected" : s === "connecting" ? "connecting" : "disconnected";
        const mapped: WhatsAppInstance[] = list
          .map((i: any) => {
            const inst = i?.instance ?? i;
            const name = inst?.name ?? inst?.instanceName ?? i?.name ?? "";
            const conn = inst?.connectionStatus ?? inst?.status ?? inst?.state;
            return {
              id: name,
              name,
              sessionId: name,
              status: mapStatus(conn),
              provedor: i?.provedor ?? "evolution",
              qrCode: null,
              phoneNumber: inst?.number ?? inst?.ownerJid ?? null,
              conversations: [],
              lastActive: new Date(),
            } as WhatsAppInstance;
          })
          .filter((x: WhatsAppInstance) => !!x.name);

        // Preserva um QR já buscado ao recarregar a lista.
        set((state) => {
          const prevQR = new Map(state.instances.map((p) => [p.id, p.qrCode]));
          return {
            instances: mapped.map((m) => ({ ...m, qrCode: prevQR.get(m.id) ?? null })),
            loading: false,
          };
        });
        // const response = await api.get(`api/whatsapp/sessions`);

        // if (response.data.success) {
        //   console.log(
        //     "[WhatsApp Store] Instâncias obtidas com sucesso!!!!!!!:",
        //     response.data.sessions
        //   );
        //   console.log(
        //     "[WhatsApp Store] Total de instâncias:",
        //     response.data.sessions.length
        //   );

        //   const instances = response.data.sessions.map((session: any) => ({
        //     id: session.id,
        //     sessionId: session.session_id,
        //     name: session.name || session.id,
        //     status: session.status,
        //     qrCode: session.qrCode,
        //     phoneNumber: session.phoneNumber,
        //     conversations: [],
        //   }));
        //   console.log("🚀 ~ instances ~ instances:", instances);

        //   // Verifique se houve mudança nas instâncias antes de atualizar o estado
        //   set((state) => {
        //     const newInstances = [...instances];
        //     if (
        //       JSON.stringify(newInstances) !== JSON.stringify(state.instances)
        //     ) {
        //       return { instances: newInstances, loading: false };
        //     }
        //     return state; // Não faz nada se não houve alteração
        //   });

        //   set({ instances, loading: false });
        // } else {
        //   console.warn(
        //     "[WhatsApp Store] Falha ao buscar instâncias:",
        //     response.data.message
        //   );
        //   set({ error: response.data.message, loading: false });
        // }
      } catch (error: any) {
        console.error(
          "[WhatsApp Store] Erro ao buscar instâncias WhatsApp:",
          error.message
        );
        set({ error: error.message, loading: false });
      }
    },

    subscribeToInstanceUpdates: (updatedInstances: WhatsAppInstance[]) => {
      try {
        console.log(
          "[WebSocket] Inscrevendo-se em atualizações de instâncias..."
        );
        set({ instances: updatedInstances });
      } catch (error: any) {
        console.error(
          "[WhatsApp Store] Erro ao atualizar instâncias:",
          error.message
        );
        set({ error: error.message, loading: false });
      }
    },

    createWhatsAppInstance: async (name: string, assistantId?: string): Promise<boolean> => {
      console.log(
        `[WhatsApp Store] Criando nova instância do WhatsApp com nome: ${name}${assistantId ? ` e assistant_id: ${assistantId}` : ''}`
      );

      set({ loading: true, error: null });

      try {
        // Gerar um ID de sessão único
        const sessionId = `session_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;
        console.log(`[WhatsApp Store] ID de sessão gerado: ${sessionId}`);

        console.log(
          `[WhatsApp Store] Enviando requisição para criar instância com sessionId: ${sessionId} e nome: ${name}`
        );
        const token = localStorage.getItem("@Piloto:token");

        const response = await api.post(`api/whatsapp/connect`, {
          sessionId,
          name,
          assistantId
        });

        console.log(`[WhatsApp Store] Resposta do servidor:`, response.data);

        if (response.data.success) {
          console.log(
            `[WhatsApp Store] Instância criada com sucesso, atualizando lista de instâncias...`
          );

          // Nova instância
          const newInstance = {
            id: sessionId,
            name,
            status: "connecting",
            qrCode: response.data.qr || null,
            phoneNumber: null,
            conversations: [],
            sessionId,
          };

          set((state) => {
            const newInstances = [...state.instances, newInstance];

            // Verifique se houve alteração nas instâncias
            if (
              JSON.stringify(newInstances) !== JSON.stringify(state.instances)
            ) {
              return {
                instances: newInstances,
                activeInstance: sessionId,
                loading: false,
              } as Partial<WhatsAppStore>; // Adicione esta asserção de tipo
            }
            return state; // Não faz nada se não houve alteração
          });

          console.log(
            `[WhatsApp Store] Iniciando polling do QR code para a nova instância: ${sessionId}`
          );
          console.log(
            `[WhatsApp Store] Conectando ao socket para a nova instância: ${sessionId}`
          );
          get().connectSocket(sessionId);

          return true;
        } else {
          const errorMessage =
            response.data.message || "Erro desconhecido ao criar instância";
          console.error(
            `[WhatsApp Store] Erro na criação da instância:`,
            errorMessage
          );

          set({
            error: errorMessage,
            loading: false,
          });

          return false;
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Erro na comunicação com o servidor";
        console.error(`[WhatsApp Store] Exceção ao criar instância:`, error);
        console.error(`[WhatsApp Store] Detalhes do erro:`, {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });

        set({
          error: errorMessage,
          loading: false,
        });

        return false;
      }
    },

    disconnectWhatsAppInstance: async (id: string) => {
      try {
        set({ loading: true, error: null });
        const response = await api.delete(`api/whatsapp/disconnect/${id}`);

        if (response.data.success) {
          get().disconnectSocket(id);

          set((state) => ({
            instances: state.instances.filter((instance) => instance.id !== id),
            loading: false,
          }));

          return true;
        } else {
          set({
            loading: false,
            error:
              response.data.message ||
              "Falha ao desconectar instância WhatsApp",
          });
          return false;
        }
      } catch (error) {
        console.error("[WhatsApp Store] Erro ao desconectar instância:", error);
        set({
          loading: false,
          error: `Erro ao desconectar instância: ${
            error instanceof Error ? error.message : "Erro desconhecido"
          }`,
        });
        return false;
      }
    },

    connectSocket: (sessionId: string) => {
      try {
        const userId = getPilotoUUID();

        if (!sessionId) {
          console.error(
            "[WhatsApp Store] Tentativa de conectar socket com sessionId undefined"
          );
          return;
        }

        // Desconectar socket existente se houver
        if (socketRef) {
          console.log(
            `[WhatsApp Store] Desconectando socket existente antes de criar novo`
          );
          socketRef.disconnect();
          socketRef = null;
        }

        // Verificar conexão atual
        if (socketStorage[sessionId]) {
          console.log(
            `[WhatsApp Store] Socket para ${sessionId} já existe, reutilizando`
          );
          socketRef = socketStorage[sessionId];
          return;
        }

        console.log(
          `[WhatsApp Store] Tentando conectar ao Socket.IO: ${sessionId}`
        );

        // Iniciar nova conexão
        socketRef = io(`${SOCKET_URL}`, {
          transports: ["websocket"],
          reconnection: false, // sem servidor socket.io na API demo — evita flood de erros no console
        });

        socketRef?.on("connect", () => {
          console.log(
            `[WhatsApp Store] Socket.IO conectado para o usuário: ${userId}`
          );
          console.log(`[WhatsApp Store] ID do socket: ${socketRef?.id}`);
          socketRef?.emit("joinUser", userId);
        });
        socketRef.on("connect_error", (error: any) => {
          console.error(`[WhatsApp Store] Erro de conexão Socket.IO:`, error);
        });

        socketRef.on("disconnect", (reason: string) => {
          console.log(`[WhatsApp Store1] Socket.IO desconectado: ${reason}`);
        });
        socketRef.on("updateMessages", (data: any, userId: string) => {
          console.log("[WhatsApp Store Message] recebido via Socket.IO para", {
            data,
          });
          // const formattedMessages = data.map((msg: any) => ({
          //   id: msg.id,
          //   fromMe: msg.fromMe,
          //   senderId: msg.fromMe ? "user" : conversationId,
          //   content: msg.text || msg.content || msg.body || "",
          //   timestamp: new Date(msg.timestamp),
          //   status: msg.status || "delivered",
          //   type: msg.type || "text",
          //   body: msg.body,
          //   audioUrl: msg.audioUrl || null,
          // }));
        });
        const sentMessages = new Set();
        // socketRef.on("aiResponse", (data: any) => {
        //   console.log(
        //     "[ WhatsApp Store AI Message] recebido via Socket.IO para",
        //     data
        //   );
        //   const uniqueKey = `${data.message.from}-${data.aiResponse}`;
        //   if (data.automaticSendMode === true && !sentMessages.has(uniqueKey)) {
        //     console.log(
        //       "[ WhatsApp Store AI Message] Enviando mensagem automática"
        //     );
        //     get().sendMessage(
        //       data.message.from,
        //       data.aiResponse,
        //       data.sessionId
        //     );
        //     get().fetchMessages(data.message.from, data.sessionId);
        //   } else {
        //     set((state) => ({
        //       newMessage: data.aiResponse,
        //     }));
        //   }
        // });

        socketRef.on("whatsapp:qrcode", (data: any) => {
          console.log(
            `[WhatsApp Store1] QR code recebido via Socket.IO para ${sessionId}`
          );

          if (data && data.qrCode) {
            console.log(
              `[WhatsApp Store1] Atualizando QR code via socket, comprimento: ${data.qrCode.length}`
            );

            // Atualiza o QR code na instância
            set((state) => ({
              instances: state.instances.map((instance) =>
                instance.id === sessionId
                  ? { ...instance, qrCode: data.qrCode }
                  : instance
              ),
            }));
          } else {
            console.warn(
              `[WhatsApp Store] Evento de QR code recebido sem dados válidos:`,
              data
            );
          }
        });

        socketRef.on("whatsapp:status", (data: any) => {
          console.log(
            `[WhatsApp Store] Status atualizado via Socket.IO:`,
            data
          );

          if (data && data.sessionId && data.status) {
            console.log(
              `[WhatsApp Store] Atualizando status para ${data.sessionId}: ${data.status}`
            );

            // Atualiza o status na instância
            set((state) => ({
              instances: state.instances.map((instance) =>
                instance.id === data.sessionId
                  ? { ...instance, status: data.status }
                  : instance
              ),
            }));
          }
        });

        // Armazena o socket para referência
        socketStorage[sessionId] = socketRef;
      } catch (error: any) {
        console.error(
          `[WhatsApp Store] Exceção ao conectar socket para ${sessionId}:`,
          error
        );
        console.error("[WhatsApp Store] Detalhes do erro:", {
          message: error.message,
          stack: error.stack,
        });
      }
    },

    disconnectSocket: (sessionId: string) => {
      if (socketStorage[sessionId]) {
        socketStorage[sessionId].disconnect();

        delete socketStorage[sessionId];
      }
    },

    fetchConversations: async (instanceId?: string) => {
      try {
        const targetInstanceId = instanceId || get().activeInstance;
        console.log(
          "🚀 ~ fetchConversations12: ~ targetInstanceId:",
          targetInstanceId
        );

        if (!targetInstanceId) {
          console.error(
            "[WhatsApp Store] Nenhuma instância ativa para buscar conversas"
          );
          set({ error: "Nenhuma instância ativa para buscar conversas" });
          return;
        }

        console.log(
          `[WhatsApp Store] Buscando conversas para a instância ${targetInstanceId}`
        );
        const response = await api.get(
          `api/whatsapp/chats/${targetInstanceId}`
        );
        console.log("🚀 ~ fetchConversations: ~ response:", response);

        if (response.data.success) {
          console.log(
            `[WhatsApp Store] Conversas obtidas com sucesso para ${targetInstanceId}`
          );

          set((state) => {
            const updatedInstances = state.instances.map((instance) =>
              instance.sessionId === targetInstanceId
                ? {
                    ...instance,
                    conversations: response.data.chats.map((chat: any) => {
                      const contact: WhatsAppContact = {
                        id: chat.id,
                        name: chat.name || `Contato ${chat.phone}`,
                        phoneNumber: `+${chat.phone}`,
                        avatar: chat.avatar,
                        isOnline: chat.isOnline || false,
                      };

                      return {
                        id: chat.id,
                        name: chat.name || chat.id,
                        phoneNumber: `+${chat.phone}`,
                        contact,
                        lastMessage: chat.lastMessage
                          ? {
                              ...chat.lastMessage,
                              timestamp: new Date(chat.lastMessageTime),
                            }
                          : undefined,
                        unreadCount: chat.unreadCount || 0,
                        loading: false,
                        error: null,
                      };
                    }),
                  }
                : instance
            );

            // Pegamos a instância atualizada para extrair as conversas
            const currentInstance = updatedInstances.find(
              (instance) => instance.sessionId === targetInstanceId
            );

            return {
              instances: updatedInstances,
              conversations: currentInstance
                ? currentInstance.conversations
                : [],
            };
          });
        } else {
          console.error(
            `[WhatsApp Store] Falha ao buscar conversas:`,
            response.data.message
          );
          set({ error: response.data.message });
        }
      } catch (error: any) {
        console.error(
          `[WhatsApp Store] Erro ao buscar conversas para ${instanceId}:`,
          error
        );
        set({
          error: error.response?.data?.message || "Erro ao buscar conversas",
        });
      }
    },

    fetchConversationsSimple: async (instanceId: string) => {
      try {
        console.log("🚀 ~ fetchConversationsSimple: ~ instanceId:", instanceId);

        if (!instanceId) {
          console.error(
            "[WhatsApp Store] Nenhuma instância ativa para buscar conversas!!!!!!!!!!!"
          );
          return null;
        }

        console.log(
          `[WhatsApp Store] Buscando conversas para a instância ${instanceId}`
        );
        const response = await api.get(`api/whatsapp/chats/${instanceId}`);
        console.log("🚀 ~ fetchConversationsSimple: ~ response:", response);

        if (response.data.success) {
          console.log(
            `[WhatsApp Store] Conversas obtidas com sucesso para ${instanceId}`
          );
          return response.data; // Retorna toda a resposta da API
        } else {
          console.error(
            `[WhatsApp Store] Falha ao buscar conversas:`,
            response.data.message
          );
          return null;
        }
      } catch (error: any) {
        console.error(
          `[WhatsApp Store] Erro ao buscar conversas para ${instanceId}:`,
          error
        );
        return null; // Retorna null em caso de erro
      }
    },
    fetchChatMessages: async (instanceId: string, chatId: string) => {
      try {
        const targetInstanceId = instanceId || get().activeInstance;

        if (!instanceId) {
          return null;
        }

        console.log(
          `[WhatsApp Store] Buscando mensagens para a instância ${instanceId}`
        );
        const response = await api.get(
          `api/whatsapp/${instanceId}/chat/${chatId}/messages`
        );

        if (response.data.success) {
          console.log(
            `[WhatsApp Store] Conversas obtidas com sucesso para ${instanceId}`
          );

          const formattedMessages = response.data.messages.map((msg: any) => ({
            id: msg.id,
            fromMe: msg.fromMe,
            senderId: msg.fromMe ? "user" : chatId,
            content: msg.text || msg.content || msg.body || "",
            timestamp: new Date(msg.timestamp),
            status: msg.status || "delivered",
            type: msg.type || "text",
            body: msg.body,
            audioUrl: msg.audioUrl || null,
          }));
      console.log("REFETCH NAS MENSAGENS");

          set((state) => {
            const updatedInstances = state.instances.map((instance) =>
              instance.sessionId === targetInstanceId
                ? {
                    ...instance,
                    conversations: instance.conversations.map((conversation) =>
                      conversation.id === chatId
                        ? {
                            ...conversation,
                            messages: formattedMessages,
                            loading: false,
                            error: null,
                          }
                        : conversation
                    ),
                  }
                : instance
            );
            console.log("213123",get().messages)
            return {
              instances: updatedInstances,
              messages: formattedMessages,
            };
          });
          return response.data; // Retorna toda a resposta da API
        } else {
          console.error(
            `[WhatsApp Store] Falha ao buscar mensagens:`,
            response.data.message
          );
          return null;
        }
      } catch (error: any) {
        console.error(
          `[WhatsApp Store] Erro ao buscar mensagens para ${instanceId}:`,
          error
        );
        return null; // Retorna null em caso de erro
      }
    },

    clearSuggestedMessage: () => {
      set((state) => ({
        suggestedMessage: "",
        newMessage: "",
      }));
    },

    handleChangeNewMessage: (newMessage: string) => {
      set((state) => ({
        newMessage,
      }));
    },

    fetchMessages: async (conversationId: string, instanceId?: string) => {
      try {
        const targetInstanceId = instanceId || get().activeInstance;

        if (!targetInstanceId) {
          console.error(
            "[WhatsApp Store] Nenhuma instância ativa para buscar mensagens"
          );
          return;
        }

        set((state) => ({
          instances: state.instances.map((instance) =>
            instance.sessionId === targetInstanceId
              ? {
                  ...instance,
                  conversations: instance.conversations.map((conversation) =>
                    conversation.id === conversationId
                      ? { ...conversation, loading: true, error: null }
                      : conversation
                  ),
                }
              : instance
          ),
        }));

        const response = await api.get(
          `api/whatsapp/${targetInstanceId}/chat/${conversationId}/messages`
        );

        if (response.data.success) {
          const formattedMessages = response.data.messages.map((msg: any) => ({
            id: msg.id,
            fromMe: msg.fromMe,
            senderId: msg.fromMe ? "user" : conversationId,
            content: msg.text || msg.content || msg.body || "",
            timestamp: new Date(msg.timestamp),
            status: msg.status || "delivered",
            type: msg.type || "text",
            body: msg.body,
            audioUrl: msg.audioUrl || null,
          }));

          set((state) => {
            const updatedInstances = state.instances.map((instance) =>
              instance.sessionId === targetInstanceId
                ? {
                    ...instance,
                    conversations: instance.conversations.map((conversation) =>
                      conversation.id === conversationId
                        ? {
                            ...conversation,
                            messages: formattedMessages,
                            loading: false,
                            error: null,
                          }
                        : conversation
                    ),
                  }
                : instance
            );

            return {
              instances: updatedInstances,
              messages: formattedMessages,
            };
          });
        } else {
          set((state) => ({
            instances: state.instances.map((instance) =>
              instance.sessionId === targetInstanceId
                ? {
                    ...instance,
                    conversations: instance.conversations.map((conversation) =>
                      conversation.id === conversationId
                        ? {
                            ...conversation,
                            loading: false,
                            error: "Erro ao carregar mensagens",
                          }
                        : conversation
                    ),
                  }
                : instance
            ),
          }));
        }
      } catch (error) {
        console.error(
          `[WhatsApp Store] Erro ao buscar mensagens para ${conversationId}:`,
          error
        );

        set((state) => ({
          instances: state.instances.map((instance) =>
            instance.sessionId === (instanceId || get().activeInstance)
              ? {
                  ...instance,
                  conversations: instance.conversations.map((conversation) =>
                    conversation.id === conversationId
                      ? {
                          ...conversation,
                          loading: false,
                          error: `Erro ao buscar mensagens: ${
                            error instanceof Error
                              ? error.message
                              : "Erro desconhecido"
                          }`,
                        }
                      : conversation
                  ),
                }
              : instance
          ),
        }));
      }
    },

    sendMessage: async (
      conversationId: string,
      content: string,
      instanceId?: string
    ) => {
      try {
        const targetInstanceId = instanceId || get().activeInstance;
        console.log("Enviando mensagem para:", targetInstanceId);

        if (!targetInstanceId) {
          console.error(
            "[WhatsApp Store] Nenhuma instância ativa para enviar mensagem"
          );
          throw new Error("Nenhuma instância ativa para enviar mensagem");
        }

        const response = await api.post(`api/whatsapp/send-message`, {
          sessionId: targetInstanceId,
          chatId: conversationId,
          message: content,
        });

        if (!response.data.success) {
          throw new Error(response.data.message || "Erro ao enviar mensagem");
        }
      } catch (error) {
        console.error(
          `[WhatsApp Store] Erro ao enviar mensagem para ${conversationId}:`,
          error
        );

        set((state) => ({
          instances: state.instances.map((instance) =>
            instance.id === (instanceId || get().activeInstance)
              ? {
                  ...instance,
                  conversations: instance.conversations.map((conversation) =>
                    conversation.id === conversationId
                      ? {
                          ...conversation,
                          error: `Erro ao enviar mensagem: ${
                            error instanceof Error
                              ? error.message
                              : "Erro desconhecido"
                          }`,
                        }
                      : conversation
                  ),
                }
              : instance
          ),
        }));

        throw error;
      }
    },

    sendAudio: async (conversationId: string, audioBlob: Blob) => {
      if (!audioBlob) return;

      const targetInstanceId: string = get().activeInstance!;

      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.ogg");
      formData.append("chatId", conversationId);
      formData.append("sessionId", targetInstanceId);

      console.log("Dados: ", formData);

      try {
        const response = await fetch(`${API_BASE_URL}/whatsapp/send-audio`, {
          method: "POST",
          body: formData,
        });

        const result = await response.json();
        console.log("Resposta do servidor:", result);
        alert(result.message || "Áudio enviado com sucesso!");
      } catch (error) {
        console.error("Erro ao enviar áudio:", error);
      }
    },

    fetchQRCode: async (sessionId: string) => {
      try {
        if (!sessionId) {
          console.error(
            "[WhatsApp Store] Tentativa de buscar QR code com sessionId undefined"
          );
          return null;
        }

        console.log(
          `[WhatsApp Store] Buscando QR code para a sessão ${sessionId} via API HTTP`
        );

        // Evolution v2: GET /instance/connect/:name → { code, base64, pairingCode }
        const response = await api.get(
          `api/whatsapp/v2/instance/connect/${sessionId}`
        );
        const d = response.data ?? {};
        const code: string | null = d.code ?? d?.instance?.code ?? null;

        if (code) {
          set((state) => ({
            instances: state.instances.map((instance) =>
              instance.id === sessionId ? { ...instance, qrCode: code } : instance
            ),
          }));
          return code;
        }
        // instância já conectada ou sem QR disponível no momento
        return null;
      } catch (error: any) {
        console.error(
          `[WhatsApp Store] Erro ao buscar QR code para ${sessionId}:`,
          error
        );
        console.error("[WhatsApp Store] Detalhes do erro:", {
          message: error.message,
          stack: error.stack,
          response: error.response
            ? {
                status: error.response.status,
                data: error.response.data,
              }
            : "Sem resposta",
        });
        return null;
      }
    },

    startQRCodePolling: (sessionId: string) => {
      // Limpar polling anterior
      get().stopQRCodePolling();

      // Verificar se o sessionId é válido antes de prosseguir
      if (!sessionId) {
        console.error(
          "[WhatsApp Store] Tentativa de iniciar polling de QR code com sessionId undefined ou nulo"
        );
        return;
      }

      console.log(
        `[WhatsApp Store] Iniciando polling de QR code para a sessão ${sessionId}`
      );

      // Buscar QR code imediatamente
      get()
        .fetchQRCode(sessionId)
        .then((qrCode) => {
          console.log(
            `[WhatsApp Store] QR code inicial obtido: ${qrCode ? "sim" : "não"}`
          );
        })
        .catch((err) => {
          console.error(
            `[WhatsApp Store] Erro ao buscar QR code inicial:`,
            err
          );
        });

      // Iniciar polling a cada 5 segundos
      qrCodePollingInterval = setInterval(async () => {
        const instance = get().instances.find((i) => i.id === sessionId);

        console.log(
          `[WhatsApp Store] Polling de QR code para ${sessionId}, status atual: ${instance?.status}`
        );

        // Se já estiver conectado, não precisa mais do QR code
        if (
          instance &&
          (instance.status === "connected" || instance.status === "ready")
        ) {
          console.log(
            `[WhatsApp Store] Interrompendo polling de QR code para ${sessionId} pois status é: ${instance.status}`
          );
          get().stopQRCodePolling();
          return;
        }

        await get().fetchQRCode(sessionId);
      }, 5000);
    },

    stopQRCodePolling: () => {
      if (qrCodePollingInterval) {
        console.log(
          `[WhatsApp Store] Interrompendo polling de QR code existente`
        );
        clearInterval(qrCodePollingInterval);
        qrCodePollingInterval = null;
      }
    },

    subscribeToQRCodeUpdates: ({
      sessionId,
      qrCode,
    }: {
      sessionId: string;
      qrCode: string | null;
    }) => {
      try {
        console.log(
          `[WebSocket] Atualizando QR Code para a sessão ${sessionId}`
        );

        set((state) => ({
          instances: state.instances.map((instance) =>
            instance.id === sessionId ? { ...instance, qrCode } : instance
          ),
        }));
      } catch (error: any) {
        console.error(
          "[WhatsApp Store] Erro ao atualizar QR Code:",
          error.message
        );
        set({ error: error.message, loading: false });
      }
    },

    // Função para atualizar as instâncias
    updateInstances: (updatedConversations: any, targetInstanceId: string) => {
      console.log("Target Instance ID:", targetInstanceId);
      console.log("Updated Conversations:", updatedConversations);
      set((state) => ({
        instances: state.instances.map((instance) =>
          instance.id === targetInstanceId
            ? {
                ...instance,
                sessionId: targetInstanceId,
                conversations: updatedConversations?.map((chat: any) => {
                  const contact: WhatsAppContact = {
                    id: chat.id,
                    name: chat.name || `Contato ${chat.phone}`,
                    phoneNumber: `+${chat.phone}`,
                    avatar: chat.avatar,
                    isOnline: chat.isOnline || false,
                  };

                  return {
                    id: chat.id,
                    name: chat.name || chat.id,
                    phoneNumber: `+${chat.phone}`,
                    contact,
                    lastMessage: chat.lastMessage
                      ? {
                          ...chat.lastMessage,
                          timestamp: new Date(chat.lastMessage.timestamp),
                        }
                      : undefined,
                    unreadCount: chat.unreadCount || 0,
                    loading: false,
                    error: null,
                  };
                }),
              }
            : instance
        ),
      }));
      console.log("Updated Instances:", get().instances);
    },

    updateHistory: (updatedHistory: any) => {
      const targetInstanceId = get().activeInstance;

      set((state) => ({
        instances: state.instances.map((instance) =>
          instance.id === targetInstanceId
            ? {
                ...instance,
                conversations: updatedHistory.map((chat: any) => {
                  const contact: WhatsAppContact = {
                    id: chat.id,
                    name: chat.name || `Contato ${chat.phone}`,
                    phoneNumber: `+${chat.phone}`,
                    avatar: chat.avatar,
                    isOnline: chat.isOnline || false,
                  };

                  return {
                    id: chat.id,
                    name: chat.name || chat.id,
                    phoneNumber: `+${chat.phone}`,
                    contact,
                    lastMessage: chat.lastMessage
                      ? {
                          ...chat.lastMessage,
                          timestamp: new Date(chat.lastMessage.timestamp),
                        }
                      : undefined,
                    unreadCount: chat.unreadCount || 0,
                    loading: false,
                    error: null,
                  };
                }),
              }
            : instance
        ),
      }));
    },
  };
});
